import Hls, { ErrorData } from "hls.js";

const defaultErrorHandler = console.log;

export class HlsHandler {
  private _hls: Hls;
  private shouldExist: boolean;

  constructor(
    videoHTML: HTMLVideoElement,
    url: string,
    secondsToSkip = 0,
    onError: (errorMessage: string) => void = defaultErrorHandler,
    shouldIndefinitelyRetry = true,
    onIsRefetchingChange: (isRefetching: boolean) => void = console.log
  ) {
    this.shouldExist = true;
    this._hls = new Hls({ autoStartLoad: false, startPosition: secondsToSkip });
    if (Hls.isSupported()) {
      this._hls.loadSource(url);
      this._hls.attachMedia(videoHTML);
      this._hls.on(Hls.Events.ERROR, (_, data) =>
        this._onHlsError(data, shouldIndefinitelyRetry, onIsRefetchingChange, onError)
      );
      this._hls.on(Hls.Events.MANIFEST_PARSED, () => this._hls.startLoad(secondsToSkip));
      return;
    }
    if (videoHTML.canPlayType("application/vnd.apple.mpegurl")) {
      videoHTML.src = url;
    }
  }

  destroy(): void {
    this.shouldExist = false;
    this._hls?.destroy();
  }

  private _onHlsError(
    data: ErrorData,
    shouldIndefinitelyRetry: boolean,
    onIsRefetchingChange: (isRefetching: boolean) => void,
    onError: (errorMessage: string) => void
  ) {
    console.warn(data);
    if (data.type !== Hls.ErrorTypes.NETWORK_ERROR) {
      return;
    }
    if (data.fatal) {
      const message = data.networkDetails.responseText;
      const extractedMessage = this._extractErrorMessage(message);
      onError(extractedMessage);
      return;
    }
    if (data.response?.code !== 404 && !shouldIndefinitelyRetry) {
      return;
    }
    this._hls.stopLoad();
    onIsRefetchingChange(true);
    setTimeout(() => {
      if (!this.shouldExist) {
        return;
      }
      onIsRefetchingChange(false);
      this._hls.startLoad();
    }, 5000);
  }

  private _extractErrorMessage(msg: string): string {
    try {
      const extractedMessage = new window.DOMParser()
        .parseFromString(msg, "text/xml")
        .getElementsByTagName("Message")[0].childNodes[0].nodeValue;
      return extractedMessage ? extractedMessage : msg;
    } catch (e) {
      return msg;
    }
  }
}
